import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./ConfirmDialog.css";

/**
 * Centred confirmation dialog with a dark overlay - the app-wide replacement for
 * antd's Popconfirm, which tethers itself to the button and ends up half off-screen
 * in a narrow panel.
 *
 * Rendered through a portal into <body>, so it is never clipped by a scrolling panel
 * or trapped inside a transformed ancestor (the canvas grid items are transformed).
 *
 *   <ConfirmDialog
 *     open={this.state.isConfirmOpen}
 *     title="Reset the drill down?"
 *     message="Every setting for this widget is cleared."
 *     onConfirm={...}
 *     onCancel={...}
 *   />
 *
 * Buttons default to Cancel / Confirm; pass confirmText / cancelText to override.
 * `danger` colours the confirm button red for destructive-and-unrecoverable actions.
 * `busy` is for while the confirmed action runs: both buttons are disabled, and
 * Escape / backdrop clicks are ignored — a second click would fire the action
 * twice, and closing mid-request would hide whether it worked.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}) {
  const confirmRef = useRef(null);

  // Nothing closes the dialog while the action is running.
  const cancel = busy ? undefined : onCancel;

  // Escape cancels, as it does in every other dialog
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") cancel?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, cancel]);

  // the page behind must not scroll away under the overlay
  useEffect(() => {
    if (!open) return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return ReactDOM.createPortal(
    // clicking the backdrop cancels; clicking the card must not bubble up to it
    <div className="confirm-dialog-overlay" onClick={cancel} role="presentation">
      <div
        className="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-dialog-body">
          <span className={`confirm-dialog-icon${danger ? " confirm-dialog-icon--danger" : ""}`} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.14" />
              <path
                d="M12 7v6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="12" cy="16.5" r="1.15" fill="currentColor" />
            </svg>
          </span>

          <div className="confirm-dialog-text">
            {title ? <h3 className="confirm-dialog-title">{title}</h3> : null}
            {message ? <p className="confirm-dialog-message">{message}</p> : null}
          </div>
        </div>

        <div className="confirm-dialog-actions">
          <button
            type="button"
            className="confirm-dialog-btn confirm-dialog-btn--cancel"
            onClick={cancel}
            disabled={busy}
          >
            {cancelText}
          </button>
          <button
            type="button"
            ref={confirmRef}
            className={`confirm-dialog-btn confirm-dialog-btn--confirm${
              danger ? " confirm-dialog-btn--danger" : ""
            }`}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
